import { useToast } from '@/hooks/useToast';
import {
  addEventOrganizer,
  getEventOrganizers,
  removeEventOrganizer,
  searchOrganizers,
} from '@/lib/api';
import type { EventOrganizer } from '@eventflow/shared-types';
import { Phone, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface EventOrganizersModalProps {
  eventId: string;
  eventName: string;
  isCreator: boolean; // Si el usuario actual es el creador del evento
  readOnly?: boolean; // True para eventos finished/archived - solo visualización
  onClose: () => void;
  onUpdate?: (args?: { eventId: string; organizers: EventOrganizer[] }) => void; // Callback cuando se actualiza la lista
}

export default function EventOrganizersModal({
  eventId,
  eventName,
  isCreator,
  readOnly = false,
  onClose,
  onUpdate,
}: EventOrganizersModalProps) {
  // En modo readOnly, deshabilitar todas las acciones de edición
  const canEdit = isCreator && !readOnly;
  const [organizers, setOrganizers] = useState<EventOrganizer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [newOrganizerEmail, setNewOrganizerEmail] = useState('');
  const [searchResults, setSearchResults] = useState<
    Array<{ id: string; email: string | null; organization_name: string; phone: string | null }>
  >([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState('');
  const searchTimeoutRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const { success, error: showError } = useToast();

  useEffect(() => {
    loadOrganizers();
  }, [eventId]);

  // Debounce para búsqueda de organizadores
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const searchTerm = newOrganizerEmail.trim();

    if (searchTerm.length < 2) {
      setSearchResults([]);
      setShowSuggestions(false);
      return;
    }

    setIsSearching(true);
    searchTimeoutRef.current = window.setTimeout(async () => {
      try {
        const results = await searchOrganizers(searchTerm, eventId);
        setSearchResults(results);
        setShowSuggestions(true);
        setError('');
      } catch (err) {
        console.error('Error searching organizers:', err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300); // Debounce de 300ms

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [newOrganizerEmail, eventId]);

  // Cerrar sugerencias al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchOrganizers = async (): Promise<EventOrganizer[]> => getEventOrganizers(eventId);

  const loadOrganizers = async (): Promise<EventOrganizer[]> => {
    try {
      setIsLoading(true);
      setError('');
      const data = await fetchOrganizers();
      setOrganizers(data);
      return data;
    } catch (err) {
      console.error('Error loading organizers:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar organizadores');
      showError('Error al cargar organizadores');
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const refreshOrganizersSilently = async () => {
    try {
      const data = await fetchOrganizers();
      setOrganizers(data);
      onUpdate?.({ eventId, organizers: data });
    } catch (err) {
      console.warn('Error refreshing organizers:', err);
    }
  };

  const handleSelectOrganizer = (email: string) => {
    const trimmedEmail = email?.trim() || '';
    console.log('[EventOrganizersModal] handleSelectOrganizer called:', { email, trimmedEmail });

    if (!trimmedEmail) {
      setError('Email no válido');
      return;
    }

    // Validar que sea un email válido antes de establecerlo
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError(`Email no válido: "${trimmedEmail}"`);
      return;
    }

    setNewOrganizerEmail(trimmedEmail);
    setShowSuggestions(false);
    setError(''); // Limpiar errores anteriores
    console.log('[EventOrganizersModal] Email set to:', trimmedEmail);
  };

  const handleAddOrganizer = async (email?: string) => {
    // Usar el email pasado como parámetro o el del input
    const rawEmail = email || newOrganizerEmail;
    const emailToAdd = rawEmail?.trim() || '';

    console.log('[EventOrganizersModal] handleAddOrganizer called:', {
      emailParam: email,
      newOrganizerEmail,
      rawEmail,
      emailToAdd,
      emailToAddLength: emailToAdd.length,
      emailToAddType: typeof emailToAdd,
    });

    if (!emailToAdd) {
      setError('Por favor ingresa el email del organizador');
      return;
    }

    // Validar formato de email básico
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValidEmail = emailRegex.test(emailToAdd);

    console.log('[EventOrganizersModal] Email validation:', {
      emailToAdd,
      emailToAddQuoted: `"${emailToAdd}"`,
      isValidEmail,
      length: emailToAdd.length,
      hasAt: emailToAdd.includes('@'),
      hasDot: emailToAdd.includes('.'),
      charCodes: emailToAdd.split('').map((c) => c.charCodeAt(0)),
    });

    if (!isValidEmail) {
      setError(
        `Por favor ingresa un email válido. Valor recibido: "${emailToAdd}" (longitud: ${emailToAdd.length})`
      );
      return;
    }

    try {
      setIsAdding(true);
      setError('');
      setShowSuggestions(false);
      const createdOrganizer = await addEventOrganizer(eventId, emailToAdd.toLowerCase());
      success('Organizador agregado exitosamente');
      setNewOrganizerEmail('');
      setSearchResults([]);

      let nextOrganizers: EventOrganizer[] = [];
      setOrganizers((prev) => {
        if (prev.some((o) => o.id === createdOrganizer.id)) {
          nextOrganizers = prev;
          return prev;
        }
        nextOrganizers = [...prev, createdOrganizer];
        return nextOrganizers;
      });
      if (nextOrganizers.length > 0) {
        onUpdate?.({ eventId, organizers: nextOrganizers });
      }
      inputRef.current?.focus();
      void refreshOrganizersSilently();
    } catch (err) {
      console.error('Error adding organizer:', err);
      const errorMessage = err instanceof Error ? err.message : 'Error al agregar organizador';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveOrganizer = async (organizerId: string, organizerName: string) => {
    if (
      !confirm(
        `¿Estás seguro de quitar a "${organizerName}" del evento?\n\nYa no podrá gestionar este evento.`
      )
    ) {
      return;
    }

    try {
      await removeEventOrganizer(eventId, organizerId);
      success('Organizador removido exitosamente');

      let nextOrganizers: EventOrganizer[] = [];
      setOrganizers((prev) => {
        nextOrganizers = prev.filter((o) => o.id !== organizerId);
        return nextOrganizers;
      });
      onUpdate?.({ eventId, organizers: nextOrganizers });
      void refreshOrganizersSilently();
    } catch (err) {
      console.error('Error removing organizer:', err);
      showError(
        'Error al remover organizador: ' +
        (err instanceof Error ? err.message : 'Error desconocido')
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {readOnly ? 'Equipo del Evento' : 'Organizadores del Evento'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {eventName}
              {readOnly && <span className="ml-2 text-xs bg-gray-200 px-2 py-0.5 rounded">Solo lectura</span>}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Cerrar"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Formulario para agregar organizador (solo para creador y si no es readOnly) */}
          {canEdit && (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Agregar Organizador</h3>
              <div className="space-y-3">
                <div className="relative">
                  <label
                    htmlFor="organizerEmail"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Email del Organizador
                  </label>
                  <div className="relative">
                    <input
                      ref={inputRef}
                      id="organizerEmail"
                      type="text"
                      value={newOrganizerEmail}
                      onChange={(e) => {
                        setNewOrganizerEmail(e.target.value);
                        setShowSuggestions(true);
                      }}
                      onFocus={() => {
                        if (searchResults.length > 0) {
                          setShowSuggestions(true);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          // Si hay sugerencias, seleccionar la primera
                          const firstResult = searchResults.find((r) => r.email);
                          if (firstResult?.email) {
                            console.log(
                              '[EventOrganizersModal] Enter pressed, selecting first result:',
                              firstResult.email
                            );
                            handleSelectOrganizer(firstResult.email);
                          } else {
                            // Si no hay sugerencias pero hay texto, intentar agregar directamente
                            console.log(
                              '[EventOrganizersModal] Enter pressed, no suggestions, trying to add:',
                              newOrganizerEmail
                            );
                            handleAddOrganizer();
                          }
                        }
                        if (e.key === 'Escape') {
                          setShowSuggestions(false);
                        }
                      }}
                      placeholder="Escribe email o nombre de organización..."
                      className="input-field w-full pr-10"
                      autoComplete="off"
                    />
                    {isSearching && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      </div>
                    )}
                  </div>

                  {/* Lista de sugerencias */}
                  {showSuggestions && searchResults.length > 0 && (
                    <div
                      ref={suggestionsRef}
                      className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    >
                      {searchResults.map((result) => (
                        <div
                          key={result.id}
                          className="w-full px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors flex items-center justify-between"
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              // Usar email si existe, sino usar organization_name como identificador
                              const identifier = result.email || result.organization_name;
                              handleSelectOrganizer(identifier);
                            }}
                            className="flex-1 text-left"
                          >
                            <div>
                              {result.email ? (
                                <p className="text-sm font-medium text-gray-900">
                                  {result.email}
                                </p>
                              ) : (
                                <p className="text-sm font-medium text-gray-900">
                                  {result.organization_name}
                                </p>
                              )}
                              {result.email && (
                                <p className="text-xs text-gray-600">{result.organization_name}</p>
                              )}
                              {result.phone && (
                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                  <Phone className="w-3 h-3" /> {result.phone}
                                </p>
                              )}
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              // Usar email si existe, sino usar organization_name
                              const identifier = result.email || result.organization_name;
                              handleAddOrganizer(identifier);
                            }}
                            className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors ml-2"
                          >
                            Agregar
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {showSuggestions &&
                    searchResults.length === 0 &&
                    newOrganizerEmail.trim().length >= 2 &&
                    !isSearching && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                        <p className="text-sm text-gray-500 text-center">
                          No se encontraron organizadores con ese término
                        </p>
                      </div>
                    )}

                  <p className="text-xs text-gray-500 mt-1">
                    Escribe al menos 2 caracteres para buscar. El usuario debe tener rol de
                    organizador.
                  </p>
                </div>
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    console.log(
                      '[EventOrganizersModal] Button clicked, current email:',
                      newOrganizerEmail
                    );
                    handleAddOrganizer();
                  }}
                  disabled={isAdding || !newOrganizerEmail.trim()}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAdding ? 'Agregando...' : (
                    <span className="flex items-center justify-center gap-2">
                      <Plus className="w-4 h-4" /> Agregar Organizador
                    </span>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Lista de organizadores */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Organizadores ({organizers.length})
            </h3>
            {isLoading ? (
              <div className="text-center py-8">
                <div className="text-gray-600">Cargando organizadores...</div>
              </div>
            ) : organizers.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <p className="text-gray-600">No hay organizadores adicionales</p>
              </div>
            ) : (
              <div className="space-y-3">
                {organizers.map((organizer) => (
                  <div
                    key={organizer.id}
                    className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:shadow-md transition-shadow"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-gray-900">
                          {organizer.organization_name}
                        </h4>
                        {organizer.is_creator && (
                          <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                            Creador
                          </span>
                        )}
                      </div>
                      {organizer.phone && (
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {organizer.phone}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {organizer.is_creator
                          ? 'Creador del evento'
                          : `Se unió el ${new Date(organizer.joined_at).toLocaleDateString(
                            'es-ES',
                            {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            }
                          )}`}
                      </p>
                    </div>
                    {canEdit && !organizer.is_creator && (
                      <button
                        onClick={() =>
                          handleRemoveOrganizer(organizer.id, organizer.organization_name)
                        }
                        className="btn-danger btn-sm ml-4"
                        title="Quitar organizador"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end">
          <button onClick={onClose} className="btn-secondary">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
